/**
 * Avatar Service - Professional Avatar Management
 * Handles avatar creation, persistence, import/export, and backup operations
 * V70 terminology compliant for useSelft Standalone
 */

import { SelfTA } from '../types'

export interface AvatarBackup {
  id: string
  avatar: SelfTA
  metadata: {
    backupDate: Date
    version: string
    source: 'manual' | 'auto' | 'import'
    fileSize?: number
    checksum: string
  }
}

export interface AvatarExportData {
  version: string
  exportDate: Date
  avatar: SelfTA
  metadata: {
    exportSource: 'useSelft-standalone'
    format: 'json'
    checksum: string
  }
}

export class AvatarService {
  private static readonly STORAGE_KEY = 'useselft-avatar-backups'
  private static readonly VERSION = '1.0.0'
  
  /**
   * Export avatar to downloadable JSON file
   */
  static async exportAvatar(avatar: SelfTA): Promise<void> {
    try {
      // Create export data structure
      const exportData: AvatarExportData = {
        version: this.VERSION,
        exportDate: new Date(),
        avatar: {
          ...avatar,
          // Ensure dates are properly serialized
          createdAt: new Date(avatar.createdAt),
          lastUpdated: new Date(avatar.lastUpdated)
        },
        metadata: {
          exportSource: 'useSelft-standalone',
          format: 'json',
          checksum: this.generateChecksum(avatar)
        }
      }

      // Create and download file
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { 
        type: 'application/json' 
      })
      
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `useSelft-avatar-${avatar.alias || 'unnamed'}-${new Date().toISOString().split('T')[0]}.json`
      
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)

      console.log('✅ Avatar exported successfully:', exportData.metadata.checksum)
    } catch (error) {
      console.error('❌ Avatar export failed:', error)
      throw new Error(`Avatar export failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Import avatar from uploaded file
   */
  static async importAvatar(file: File): Promise<SelfTA> {
    return new Promise((resolve, reject) => {
      if (!file.name.endsWith('.json')) {
        reject(new Error('Invalid file format. Please select a JSON file.'))
        return
      }

      const reader = new FileReader()
      
      reader.onload = (event) => {
        try {
          const content = event.target?.result as string
          const importData = JSON.parse(content) as AvatarExportData
          
          // Validate import data structure
          if (!this.validateImportData(importData)) {
            throw new Error('Invalid avatar file format')
          }

          // Verify checksum if available
          if (importData.metadata?.checksum) {
            const currentChecksum = this.generateChecksum(importData.avatar)
            if (currentChecksum !== importData.metadata.checksum) {
              console.warn('⚠️ Checksum mismatch - file may be corrupted or modified')
            }
          }

          // Convert date strings back to Date objects
          const avatar: SelfTA = {
            ...importData.avatar,
            createdAt: new Date(importData.avatar.createdAt),
            lastUpdated: new Date(importData.avatar.lastUpdated)
          }

          console.log('✅ Avatar imported successfully:', avatar.alias)
          resolve(avatar)
          
        } catch (error) {
          console.error('❌ Avatar import failed:', error)
          reject(new Error(`Failed to import avatar: ${error instanceof Error ? error.message : 'Invalid file format'}`))
        }
      }

      reader.onerror = () => {
        reject(new Error('Failed to read file'))
      }

      reader.readAsText(file)
    })
  }

  /**
   * Create backup of current avatar
   */
  static async backupAvatar(avatar: SelfTA, source: AvatarBackup['metadata']['source'] = 'manual'): Promise<string> {
    try {
      const backupId = `backup_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      
      const backup: AvatarBackup = {
        id: backupId,
        avatar: { ...avatar },
        metadata: {
          backupDate: new Date(),
          version: this.VERSION,
          source,
          checksum: this.generateChecksum(avatar)
        }
      }

      // Get existing backups
      const backups = this.getStoredBackups()
      
      // Add new backup and limit to last 10
      backups.push(backup)
      const limitedBackups = backups.slice(-10)

      // Store updated backups
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(limitedBackups))
      
      console.log('✅ Avatar backup created:', backupId)
      return backupId
      
    } catch (error) {
      console.error('❌ Avatar backup failed:', error)
      throw new Error(`Avatar backup failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Restore avatar from backup
   */
  static async restoreBackup(backupId: string): Promise<SelfTA> {
    try {
      const backups = this.getStoredBackups()
      const backup = backups.find(b => b.id === backupId)
      
      if (!backup) {
        throw new Error(`Backup not found: ${backupId}`)
      }

      // Verify backup integrity
      const currentChecksum = this.generateChecksum(backup.avatar)
      if (currentChecksum !== backup.metadata.checksum) {
        console.warn('⚠️ Backup checksum mismatch - data may be corrupted')
      }

      console.log('✅ Avatar restored from backup:', backupId)
      return { ...backup.avatar }
      
    } catch (error) {
      console.error('❌ Avatar restore failed:', error)
      throw new Error(`Avatar restore failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  /**
   * Get all stored backups
   */
  static getBackups(): AvatarBackup[] {
    return this.getStoredBackups()
  }

  /**
   * Delete specific backup
   */
  static deleteBackup(backupId: string): boolean {
    try {
      const backups = this.getStoredBackups()
      const filteredBackups = backups.filter(b => b.id !== backupId)
      
      if (filteredBackups.length === backups.length) {
        return false // Backup not found
      }

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filteredBackups))
      console.log('✅ Backup deleted:', backupId)
      return true
      
    } catch (error) {
      console.error('❌ Backup deletion failed:', error)
      return false
    }
  }

  /**
   * Clear all backups
   */
  static clearAllBackups(): boolean {
    try {
      localStorage.removeItem(this.STORAGE_KEY)
      console.log('✅ All backups cleared')
      return true
    } catch (error) {
      console.error('❌ Failed to clear backups:', error)
      return false
    }
  }

  /**
   * Generate checksum for avatar data
   */
  private static generateChecksum(avatar: SelfTA): string {
    const data = JSON.stringify({
      alias: avatar.alias,
      displayName: avatar.displayName,
      statusMessage: avatar.statusMessage,
      profileImage: avatar.profileImage
    })
    
    // Simple hash function for checksum
    let hash = 0
    for (let i = 0; i < data.length; i++) {
      const char = data.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Convert to 32-bit integer
    }
    
    return Math.abs(hash).toString(16)
  }

  /**
   * Get backups from localStorage
   */
  private static getStoredBackups(): AvatarBackup[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY)
      if (!stored) return []
      
      const backups = JSON.parse(stored) as AvatarBackup[]
      
      // Convert date strings back to Date objects
      return backups.map(backup => ({
        ...backup,
        metadata: {
          ...backup.metadata,
          backupDate: new Date(backup.metadata.backupDate)
        },
        avatar: {
          ...backup.avatar,
          createdAt: new Date(backup.avatar.createdAt),
          lastUpdated: new Date(backup.avatar.lastUpdated)
        }
      }))
      
    } catch (error) {
      console.error('❌ Failed to load backups:', error)
      return []
    }
  }

  /**
   * Validate imported avatar data
   */
  private static validateImportData(data: any): data is AvatarExportData {
    if (!data || typeof data !== 'object') return false
    
    // Check required top-level properties
    if (!data.avatar || !data.version || !data.exportDate) return false
    
    // Validate avatar structure
    const avatar = data.avatar
    if (!avatar.id || !avatar.alias || !avatar.displayName) return false
    if (!avatar.createdAt || !avatar.lastUpdated) return false
    
    // Check required avatar properties
    const requiredProps = ['id', 'alias', 'displayName', 'statusMessage']
    const hasAllProps = requiredProps.every(prop => prop in avatar)
    
    return hasAllProps
  }

  /**
   * Create default avatar with generated SVG image
   */
  static createDefaultAvatar(): SelfTA {
    const timestamp = new Date()
    const randomId = Math.random().toString(36).substr(2, 12)

    // Create a simple SVG avatar directly
    const defaultAvatarSVG = this.createDefaultAvatarSVG()

    return {
      id: `useselft_${randomId}`,
      alias: `user_${randomId.slice(-6)}`,
      displayName: 'useSelft User',
      statusMessage: 'Ready for P2P connections',
      profileImage: defaultAvatarSVG,
      createdAt: timestamp,
      lastUpdated: timestamp
    }
  }

  /**
   * Create a default SVG avatar
   */
  private static createDefaultAvatarSVG(): string {
    const svg = `<svg width="100" height="100" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="Default user avatar">
  <defs>
    <style>
      .bg { fill: #3b82f6; }
      .text {
        fill: #ffffff;
        font-family: system-ui, -apple-system, sans-serif;
        font-size: 14px;
        text-anchor: middle;
        dominant-baseline: middle;
        font-weight: 500;
      }
    </style>
  </defs>
  <circle cx="50" cy="50" r="40" class="bg"/>
  <text x="50" y="50" class="text">US</text>
</svg>`

    try {
      const base64 = btoa(unescape(encodeURIComponent(svg)))
      return `data:image/svg+xml;base64,${base64}`
    } catch (error) {
      console.error('Failed to create default avatar SVG:', error)
      return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
    }
  }

  /**
   * Validate avatar data
   */
  static validateAvatar(avatar: Partial<SelfTA>): { valid: boolean; errors: string[] } {
    const errors: string[] = []
    
    if (!avatar.alias?.trim()) {
      errors.push('Alias is required')
    } else if (avatar.alias.length > 20) {
      errors.push('Alias must be 20 characters or less')
    } else if (!/^[a-zA-Z0-9_-]+$/.test(avatar.alias)) {
      errors.push('Alias can only contain letters, numbers, underscores, and hyphens')
    }
    
    if (!avatar.displayName?.trim()) {
      errors.push('Display name is required')
    } else if (avatar.displayName.length > 50) {
      errors.push('Display name must be 50 characters or less')
    }
    
    if (avatar.statusMessage && avatar.statusMessage.length > 100) {
      errors.push('Status message must be 100 characters or less')
    }
    
    return {
      valid: errors.length === 0,
      errors
    }
  }
}